import type { ReactNode } from 'react';
import { AgentDot } from '../../AgentDot';
import { WorkspaceBadge } from '../WorkspaceBadge';
import { useWorkspaces } from '../../../lib/workspaces';
import { OverflowMenu, type OverflowMenuAction } from '../ui';

// The ONE filter dropdown every category page uses for its Space and agent axes (Figma 21055:247908 /
// 21055:248158): a ghost "All spaces ⌄" trigger, then "All …" and each option with its mark — the Space's
// badge, the agent's dot. Tasks, Routines, Goals and Files all draw it, so the menus cannot drift apart.

export interface FilterMenuOption {
  id: string;
  name: string;
  /** The 16px mark drawn before the name. */
  icon?: ReactNode;
}

/** The Space badge for each option of one menu. One roster read for the whole menu: a hook per option would
 *  put a workspace subscription (and its refetch on every workspace event) under every row. */
export function useSpaceMark(): (id: string, name: string) => ReactNode {
  const state = useWorkspaces();
  return (id, name) => {
    const icon = state.status === 'ready' ? state.workspaces.find((space) => space.id === id)?.icon : undefined;
    return <WorkspaceBadge ws={{ id, name, icon }} size={16} />;
  };
}
export const agentMark = (id: string): ReactNode => <AgentDot id={id} size={16} />;

export function CategoryFilterMenu({
  options,
  selectedId,
  allLabel,
  ariaLabel,
  onPick,
  onOpen,
}: {
  options: FilterMenuOption[];
  /** '' is the "All …" rest. */
  selectedId: string;
  allLabel: string;
  ariaLabel: string;
  onPick: (id: string) => void;
  onOpen?: () => void;
}) {
  const selected = options.find((option) => option.id === selectedId);
  const actions: OverflowMenuAction[] = [
    { key: '', label: allLabel, onSelect: () => onPick('') },
    ...options.map((option) => ({ key: option.id, label: option.name, icon: option.icon, onSelect: () => onPick(option.id) })),
  ];
  return (
    <OverflowMenu
      label={selected?.name ?? allLabel}
      ariaLabel={ariaLabel}
      actions={actions}
      selectedKey={selected ? selected.id : ''}
      triggerVariant="text"
      triggerSize="small"
      onOpen={onOpen}
    />
  );
}
